import { FC, useMemo } from 'react';
import moment from 'moment';
import { DataTable, DataTableColumnDef } from 'components/data-table';
import type { PriceDeckRow } from './types';
import { formatPrice } from './utils';
import styles from './price-deck-schedule-table.module.scss';

interface TableRow extends Record<string, unknown> {
  id: string;
  month: string;
  oilPrice: number;
  gasPrice: number;
  source: string;
}

interface PriceDeckScheduleTableProps {
  rows: PriceDeckRow[];
}

// Display label for the Month column. Kept out of the row data so the raw ISO
// month stays the accessor (and export) value — see the `month` column's cell.
const formatMonth = (month: string): string =>
  moment.utc(month).format('MMM YY');

export const PriceDeckScheduleTable: FC<PriceDeckScheduleTableProps> = ({
  rows: apiRows,
}) => {
  const rows: TableRow[] = useMemo(
    () =>
      apiRows.map((row) => ({
        id: row.month,
        // Hold the raw ISO month (YYYY-MM-DD); format only for display in the
        // column cell. Baking 'MMM YY' in here also fed the CSV export, which
        // Excel then read as a day-of-month, losing the year (INS-12335).
        month: row.month,
        oilPrice: row.oilPrice,
        gasPrice: row.gasPrice,
        source: row.source,
      })),
    [apiRows]
  );

  const columns: DataTableColumnDef<TableRow>[] = useMemo(
    () => [
      {
        accessorKey: 'month',
        header: 'Month',
        cell: ({ getValue }) => formatMonth(getValue() as string),
      },
      {
        accessorKey: 'oilPrice',
        header: 'Oil ($/BBL)',
        cell: ({ getValue }) => formatPrice(getValue() as number | null),
      },
      {
        accessorKey: 'gasPrice',
        header: 'Gas ($/MMBTU)',
        cell: ({ getValue }) => formatPrice(getValue() as number | null),
      },
      {
        accessorKey: 'source',
        header: 'Source',
      },
    ],
    []
  );

  return (
    <div className={styles.scheduleWrapper}>
      <DataTable
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        scroll={{ mode: 'paginated' }}
        config={{
          export: {
            exportPresets: [
              {
                name: 'Export',
                format: 'csv',
                filename: 'price-deck-schedule',
              },
            ],
          },
          columnPinning: false,
          columnReordering: false,
          columnResizing: false,
        }}
        searchPlaceholder="Search..."
      />
    </div>
  );
};
